import { Cookie as CookieNames } from '@ps/web-core-enums';
import { canUseDOM } from '@ps/web-core-utils';
import { format } from 'date-fns';
import Cookies from 'universal-cookie';

import type { Model, Slicer } from '../../types/index';

import timeZoneAbbreviations from './time-zone-abbreviations';

const cookies = new Cookies();

// ie `Europe/Copenhagen`
export type TimeZoneState = string;

const isValidTimeZone = (timeZone: string | undefined) => {
  try {
    new Intl.DateTimeFormat(undefined, {
      timeZone,
      timeZoneName: 'long'
    });
    return true;
  } catch {
    return false;
  }
};

const getTimeZonePart = (
  timeZone: string,
  timeZoneName: Intl.DateTimeFormatOptions['timeZoneName']
) => {
  const formatter = new Intl.DateTimeFormat(undefined, {
    timeZone: isValidTimeZone(timeZone) ? timeZone : 'UTC',
    timeZoneName
  });
  const parts = formatter.formatToParts(new Date());
  // By setting `timeZoneName` in the options you will always receive a `timeZoneName`
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return parts.find(({ type }) => type === 'timeZoneName')!.value;
};

const reducers = {
  setTimeZone: (state: TimeZoneState, payload: string) => {
    const timeZone = isValidTimeZone(payload) ? payload : 'UTC';
    if (canUseDOM) {
      // Save the time zone to a cookie in order to send it to the vader on first render
      cookies.set(CookieNames.CLIENT_TIME_ZONE, timeZone, {
        path: '/',
        encode: (cookie) => cookie // By default `encode` calls `encodeURIComponent` but this breaks the `/`
      });
    }
    return timeZone;
  }
};

const selectors = (slice: Slicer<TimeZoneState>) => ({
  /**
   * @returns {string} Time zone i.e. America/New_York
   */
  getTimeZoneName() {
    return slice;
  },
  /**
   * @returns {string} Short time zone i.e. EST
   */
  getTimeZone() {
    return slice(
      (state) =>
        timeZoneAbbreviations[
          getTimeZonePart(state, 'long') as keyof typeof timeZoneAbbreviations
        ] ?? getTimeZonePart(state, 'short')
    );
  },
  /**
   * @returns {string} Offset from GMT i.e. GMT+1
   */
  getGmtOffset() {
    return slice(() => format(new Date(), 'O'));
  }
});

export interface TimeZoneModel
  extends Model<TimeZoneState, typeof reducers, void, void, typeof selectors> {
  reducers: typeof reducers;
  selectors: typeof selectors;
}

const timeZone: TimeZoneModel = {
  state: Intl.DateTimeFormat().resolvedOptions().timeZone,
  reducers,
  selectors
};

export default timeZone;
